import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import withApi from '../../components/higherOrderComponents/withApi';
import Modal from '../components/Modal';
import { useModal } from '../components/useModal';

/**
 * The pairings board — who plays on which court, for one session.
 *
 * Held back from the first rewrite for a good reason: it moves people by drag
 * and drop, and the way that goes wrong is a move that *looks* applied and was
 * never saved, discovered by four people standing on the wrong court.
 *
 * **So there is no optimistic update here, on purpose.** A drop sends the PATCH,
 * waits for it, and re-reads the session. The board therefore shows what the
 * server has and never what the browser hopes — a slow move looks slow, which is
 * the honest failure. The old page works this way too; it is the one part of it
 * worth copying exactly.
 *
 * What is *not* copied is the two thirds of its drag that never worked. Its own
 * header says "drag a roster row onto a court slot to place; drag between slots
 * to swap; drag back to the roster to remove", and of those three:
 *
 *   - **swap between slots** works, and is the whole feature;
 *   - **roster → court** hits `alert('Not placed yet…')`, because placing
 *     somebody new needs a POST nobody wrote — the old page says so in a comment;
 *   - **court → roster** does nothing at all: the roster has no drop handler.
 *
 * Offering a drag that fails is worse than not offering it, so an unplaced player
 * is not draggable and the panel says why once, instead of every attempt ending
 * in an alert. When the POST exists, this is where it goes.
 *
 * Two more things the old page did that this cannot: `window.confirm` before
 * sending real emails, and a popup window for the preview. The send is a dialog
 * that says how many people it writes to, and the preview renders in the modal's
 * iframe — no popup to be blocked, and it stays inside the tree.
 */

const SLOTS_PER_COURT = 4;

const fmtRange = (from, to) => {
  const start = from ? new Date(from) : null;
  const end = to ? new Date(to) : null;
  if (!start || Number.isNaN(start.getTime())) return '—';
  const day = start.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
  const t = d => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return end && !Number.isNaN(end.getTime())
    ? `${day} · ${t(start)}–${t(end)}`
    : `${day} · ${t(start)}`;
};

/** What a court reads as, once you look at who is on it. */
const courtMeta = players => {
  if (players.length === 0) return 'Empty';
  const genders = players.map(p => p.gender).filter(Boolean);
  const levels = players.map(p => p.level || 3);
  const min = Math.min(...levels);
  const max = Math.max(...levels);

  const mix = genders.length === 0
    ? '—'
    : (genders.every(g => g === 'Female') ? 'All women'
      : (genders.every(g => g === 'Male') ? 'All men' : 'Mixed'));

  return `${mix} · level ${min === max ? min : `${min}–${max}`}`;
};

const genderTone = gender => (gender === 'Female' ? 'pink' : (gender === 'Male' ? 'blue' : 'neutral'));

function Player({ player, compact }) {
  if (!player) return <span className="next-muted">—</span>;
  return (
    <>
      <span className={compact ? 'next-stack__main' : 'next-pairing__name'}>
        {player.name || player.email}
      </span>
      <span className="next-pairing__tags">
        <span className={`next-pill next-pill--${genderTone(player.gender)}`}>{player.gender || '—'}</span>
        <span className="next-pill next-pill--neutral">L{player.level || '?'}</span>
        {player.partnerEmail && (
          <span className="next-pairing__partner" title={`Asked to play with ${player.partnerEmail}`}>
            ↔ {player.partnerEmail.split('@')[0]}
          </span>
        )}
      </span>
    </>
  );
}

Player.propTypes = { player: PropTypes.object, compact: PropTypes.bool };

const PlayEventSessionInternal = ({ api }) => {
  const { eventId, sessionId } = useParams();
  const { ask, modalProps } = useModal();

  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [note, setNote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(null);
  const [preview, setPreview] = useState(undefined);

  const load = useCallback(async () => {
    try {
      const session = await api.playEvents.getSession(sessionId);
      if (!session || !session.roster) {
        setFailed(true);
        return;
      }
      setData(session);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <Link className="next-back" to={`/next/play-event/${eventId}`}>← Back to the event</Link>
        <div className="next-note next-note--error">
          Could not load this session.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!data) return <div className="next-page next-muted">Loading…</div>;

  const placements = data.pairings?.placements ?? [];
  const placedByReg = new Map(placements.map(p => [p.registrationId, p]));
  const playerByReg = new Map((data.roster || []).map(r => [r.registrationId, r]));

  // Court → its four slots, indexed from 1 as the payload numbers them.
  const courts = [];
  for (let court = 1; court <= (data.courtCount || 0); court += 1) {
    const slots = Array(SLOTS_PER_COURT).fill(null);
    placements
      .filter(p => p.courtIndex === court)
      .forEach(p => { slots[p.slotIndex - 1] = p; });
    courts.push({ court, slots });
  }

  const move = async (registrationId, courtIndex, slotIndex) => {
    const existing = placedByReg.get(registrationId);

    // Should be unreachable — an unplaced player is not draggable — but a drag
    // that started before a reload could still arrive here.
    if (!existing) {
      setNote({
        tone: 'error',
        text: 'That player is not on a court yet, and there is no way to place somebody new from here. Generate pairings first.'
      });
      return;
    }
    if (existing.courtIndex === courtIndex && existing.slotIndex === slotIndex) return;

    setBusy(true);
    setNote(null);
    try {
      await api.playEvents.movePairing(existing.id, { courtIndex, slotIndex });
      // Re-read rather than patching state in place: what is on screen after a
      // move is what the server says, or the move did not happen.
      await load();
    } catch (error) {
      setNote({ tone: 'error', text: `The move was not saved. ${error?.message || ''}`.trim() });
      await load();
    } finally {
      setBusy(false);
      setDragging(null);
    }
  };

  const onDrop = (event, courtIndex, slotIndex) => {
    event.preventDefault();
    const registrationId = event.dataTransfer.getData('text/plain');
    if (registrationId) move(registrationId, courtIndex, slotIndex);
  };

  const generate = () => ask({
    kind: data.pairings ? 'danger' : 'confirm',
    title: data.pairings ? 'Generate pairings again?' : 'Generate the pairings?',
    context: 'Pairings',
    body: data.pairings
      ? 'Every court is laid out again from scratch, by level and gender. Any placement moved by hand is replaced.'
      : 'Players are laid out across the courts by level and gender. You can then move anybody by dragging.',
    warning: data.pairings ? 'Hand-made changes to this board are lost.' : undefined,
    confirmLabel: data.pairings ? 'Generate again' : 'Generate',
    onConfirm: async () => {
      setBusy(true);
      try {
        // Strategy 1 is GenderSegregatedLevelBalanced, the only one the old page
        // offers and the only one named in the api module.
        await api.playEvents.generatePairings(sessionId, { strategy: 1 });
        await load();
        setNote({ tone: 'info', text: 'Pairings generated.' });
      } catch (error) {
        setNote({ tone: 'error', text: `Unable to generate the pairings. ${error?.message || ''}`.trim() });
        throw error;
      } finally {
        setBusy(false);
      }
    }
  });

  const send = () => ask({
    kind: 'danger',
    title: `Email these pairings to ${data.roster.length} player${data.roster.length === 1 ? '' : 's'}?`,
    context: 'Send pairings',
    body: 'Every confirmed player is written to now, with their court and the people they are playing with. The emails go out while you wait — there is no queue to stop.',
    warning: 'An email that has gone cannot be recalled.',
    confirmLabel: 'Send now',
    onConfirm: async () => {
      setBusy(true);
      try {
        const result = await api.playEvents.sendPairingsEmail(sessionId);
        // The endpoint's own four numbers, unsummarised: "sent 12" alone hides
        // the three it could not reach.
        setNote({
          tone: (result?.failed ?? 0) > 0 ? 'error' : 'info',
          text: `Attempted ${result?.attempted ?? '?'} · sent ${result?.sent ?? '?'} · skipped ${result?.skipped ?? '?'} · failed ${result?.failed ?? '?'}.`
        });
      } catch (error) {
        setNote({ tone: 'error', text: `The emails were not sent. ${error?.message || ''}`.trim() });
        throw error;
      } finally {
        setBusy(false);
      }
    }
  });

  const showPreview = async registrationId => {
    const player = playerByReg.get(registrationId);
    setPreview(null);

    ask({
      kind: 'confirm',
      title: 'The email this player will get',
      context: player ? (player.name || player.email) : 'Preview',
      body: 'Rendered by the same code that sends it, for this player — their court, their teammates, their language.',
      confirmLabel: 'Close',
      dismissOnly: true
    });

    try {
      const result = await api.playEvents.previewPairing(sessionId, registrationId);
      setPreview(result?.html ?? '');
    } catch (error) {
      setPreview('');
      setNote({ tone: 'error', text: `Unable to render the preview. ${error?.message || ''}`.trim() });
    }
  };

  const placedCount = placements.length;

  return (
    <div className="next-page next-page--wide">
      <Link className="next-back" to={`/next/play-event/${eventId}`}>← Back to the event</Link>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{data.eventNameEn || 'Session'}</h1>
          {data.sport && <span className="next-pill next-pill--neutral">{data.sport}</span>}
        </div>
        <span className="next-detail__id">{fmtRange(data.startsAtUtc, data.endsAtUtc)}</span>
      </header>

      <div className="next-page__header next-page__header--row">
        <p className="next-muted">
          {data.roster.length} of {data.capacity} signed up · {data.courtCount} courts ·{' '}
          {data.pairings
            ? `${placedCount} placed`
            : 'no pairings yet'}
        </p>
        <div className="next-page__actions">
          <button type="button" className="next-btn" disabled={busy} onClick={generate}>
            {data.pairings ? 'Generate again' : 'Generate pairings'}
          </button>
          <button
            type="button"
            className="next-btn next-btn--primary"
            disabled={busy || !data.pairings}
            title={data.pairings ? undefined : 'There are no pairings to send yet'}
            onClick={send}
          >
            Send pairings email
          </button>
        </div>
      </div>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail next-detail--board">
        <div className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head">
              <h2>Roster</h2>
              <span className="next-muted">{data.roster.length} / {data.capacity}</span>
            </div>

            {/* Said once, here, instead of after every failed drag. */}
            <p className="next-form__hint next-pairing__hint">
              Players already on a court can be dragged to another slot. Placing somebody
              new is not possible from this admin — generate the pairings first.
            </p>

            <div className="next-pairing__roster">
              {data.roster.map(player => {
                const placed = placedByReg.has(player.registrationId);
                return (
                  <div
                    key={player.registrationId}
                    className={`next-pairing__player${placed ? ' next-pairing__player--placed' : ''}`}
                    draggable={placed && !busy}
                    onDragStart={event => {
                      event.dataTransfer.setData('text/plain', player.registrationId);
                      setDragging(player.registrationId);
                    }}
                    onDragEnd={() => setDragging(null)}
                  >
                    <div className="next-pairing__row">
                      <Player player={player} compact />
                      {placed && (
                        <button
                          type="button"
                          className="next-btn next-btn--sm next-btn--quiet"
                          onClick={() => showPreview(player.registrationId)}
                        >
                          Preview
                        </button>
                      )}
                    </div>
                    {player.levelText && <span className="next-stack__sub">{player.levelText}</span>}
                  </div>
                );
              })}
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head">
              <h2>Watchlist</h2>
              <span className="next-muted">{data.watchlist?.length ?? 0}</span>
            </div>
            <p className="next-form__hint next-pairing__hint">
              When a spot opens everybody here is told at once — first to click takes it.
            </p>
            {(data.watchlist ?? []).map(person => (
              <div className="next-pairing__player" key={person.userId}>
                <span className="next-stack__main">{person.name || person.email}</span>
                <span className="next-pairing__tags">
                  <span className={`next-pill next-pill--${genderTone(person.gender)}`}>{person.gender || '—'}</span>
                  <span className="next-pill next-pill--neutral">L{person.level || '?'}</span>
                </span>
              </div>
            ))}
            {(data.watchlist ?? []).length === 0 && (
              <p className="next-table__note next-muted">Nobody is waiting.</p>
            )}
          </section>
        </div>

        <div className="next-detail__main">
          {!data.pairings ? (
            <section className="next-card">
              <p className="next-table__note next-muted">
                No pairings yet. Generate them to lay the courts out, then drag to adjust.
              </p>
            </section>
          ) : (
            <div className="next-courts">
              {courts.map(({ court, slots }) => {
                const players = slots.filter(Boolean).map(p => playerByReg.get(p.registrationId)).filter(Boolean);

                return (
                  <section className="next-card next-court" key={court}>
                    <div className="next-card__head">
                      <h2>Court {court}</h2>
                      <span className="next-muted">{slots.filter(Boolean).length}/{SLOTS_PER_COURT}</span>
                    </div>

                    <div className="next-court__slots">
                      {slots.map((placement, index) => {
                        const slotIndex = index + 1;
                        const player = placement ? playerByReg.get(placement.registrationId) : null;
                        const isDragged = placement && dragging === placement.registrationId;

                        return (
                          <div
                            key={`${court}-${slotIndex}`}
                            className={[
                              'next-court__slot',
                              placement ? 'next-court__slot--taken' : 'next-court__slot--free',
                              placement?.manuallyEdited ? 'next-court__slot--edited' : '',
                              isDragged ? 'next-court__slot--dragging' : ''
                            ].filter(Boolean).join(' ')}
                            draggable={!!placement && !busy}
                            onDragStart={event => {
                              event.dataTransfer.setData('text/plain', placement.registrationId);
                              setDragging(placement.registrationId);
                            }}
                            onDragEnd={() => setDragging(null)}
                            onDragOver={event => event.preventDefault()}
                            onDrop={event => onDrop(event, court, slotIndex)}
                          >
                            {placement
                              ? <Player player={player} />
                              : <span className="next-court__empty">empty</span>}
                          </div>
                        );
                      })}
                    </div>

                    <div className="next-court__meta">{courtMeta(players)}</div>
                  </section>
                );
              })}
            </div>
          )}

          <section className="next-card">
            <div className="next-card__head"><h2>About these pairings</h2></div>
            <div className="next-facts next-facts--single">
              <div className="next-fact">
                <span className="next-fact__label">Generated</span>
                <span className="next-fact__value">
                  {data.pairings?.generatedAtUtc
                    ? new Date(data.pairings.generatedAtUtc).toLocaleString()
                    : <span className="next-muted">never</span>}
                </span>
              </div>
              <div className="next-fact">
                <span className="next-fact__label">Strategy</span>
                <span className="next-fact__value">
                  {data.pairings?.strategy || <span className="next-muted">—</span>}
                </span>
              </div>
            </div>
            <p className="next-form__hint next-pairing__hint">
              The pairings email is scheduled on its own two hours before kickoff. A late
              cancellation inside that window re-pairs and resends, up to ten minutes
              before the start.
            </p>
          </section>
        </div>
      </div>

      {/* `previewHtml` only while the preview dialog is the one open, so the other
          dialogs do not grow an empty frame. */}
      <Modal
        {...modalProps}
        previewHtml={modalProps.title === 'The email this player will get' ? preview : undefined}
        dismissOnly={modalProps.dismissOnly}
      />
    </div>
  );
};

PlayEventSessionInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const PlayEventSession = withApi(PlayEventSessionInternal);

PlayEventSession.requiresLogin = true;

export default PlayEventSession;
